import { Bell, CheckCheck, Clock, X, Trash2 } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { notificationsAPI } from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Get notifications
  const { data: notificationsData } = useQuery(
    'notifications',
    notificationsAPI.getAll,
    {
      refetchInterval: 30000, // Refetch every 30 seconds
      refetchOnWindowFocus: true,
    }
  );

  // Get unread count
  const { data: unreadCountData } = useQuery(
    'unread-count',
    notificationsAPI.getUnreadCount,
    {
      refetchInterval: 30000,
      refetchOnWindowFocus: true,
    }
  );

  const notifications = notificationsData?.data?.data || [];
  const unreadCount = unreadCountData?.data?.count || 0;

  // Mark as read mutation
  const markAsReadMutation = useMutation(
    (id) => notificationsAPI.markAsRead(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('unread-count');
      },
    }
  );

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation(
    () => notificationsAPI.markAllAsRead(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('unread-count');
      },
    }
  );

  // Delete notification mutation
  const deleteNotificationMutation = useMutation(
    (id) => notificationsAPI.delete(id),
    {
      onMutate: async (id) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries('notifications');
        
        // Snapshot the previous value
        const previousNotifications = queryClient.getQueryData('notifications');
        
        // Optimistically update the UI - handle different response structures
        queryClient.setQueryData('notifications', (oldData) => {
          if (!oldData) return oldData;
          
          // Handle different response structures
          let notifications = [];
          if (Array.isArray(oldData?.data?.data)) {
            notifications = oldData.data.data;
          } else if (Array.isArray(oldData?.data)) {
            notifications = oldData.data;
          } else if (Array.isArray(oldData)) {
            notifications = oldData;
          }
          
          const filtered = notifications.filter(n => n.id !== id);
          
          // Return in the same structure as received
          if (oldData?.data?.data) {
            return {
              ...oldData,
              data: {
                ...oldData.data,
                data: filtered
              }
            };
          } else if (oldData?.data) {
            return {
              ...oldData,
              data: filtered
            };
          } else {
            return filtered;
          }
        });
        
        // Also update unread count optimistically
        queryClient.setQueryData('unread-count', (oldData) => {
          if (!oldData) return oldData;
          const notification = (previousNotifications?.data?.data || previousNotifications?.data || []).find(n => n.id === id);
          const currentCount = oldData?.data?.count || oldData?.count || 0;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              count: notification && !notification.is_read ? Math.max(0, currentCount - 1) : currentCount
            }
          };
        });
        
        return { previousNotifications };
      },
      onSuccess: () => {
        // Invalidate and refetch queries to ensure consistency
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('unread-count');
      },
      onError: (error, id, context) => {
        // Rollback on error
        if (context?.previousNotifications) {
          queryClient.setQueryData('notifications', context.previousNotifications);
        }
        console.error('Error deleting notification:', error);
        toast.error(error?.response?.data?.message || 'Failed to delete notification');
      },
    }
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_booking':
        return <Clock className="w-5 h-5 text-blue-600" />;
      case 'booking_confirmed':
        return <CheckCheck className="w-5 h-5 text-green-600" />;
      case 'booking_cancelled':
        return <X className="w-5 h-5 text-red-600" />;
      default:
        return <Bell className="w-5 h-5 text-gray-600" />;
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.is_read) {
      markAsReadMutation.mutate(notification.id);
    }

    // Close dropdown
    setIsOpen(false);

    // Navigate based on user role and notification type
    const isAdmin = user?.role === 'super_admin' || user?.role === 'court_admin' || user?.role === 'manager';

    if (notification.type === 'new_booking') {
      // Admin receives new booking notification -> go to admin bookings
      if (isAdmin) {
        navigate('/admin/bookings');
      }
    } else if (notification.type === 'booking_confirmed' || notification.type === 'booking_cancelled') {
      // User receives confirmation/cancellation -> go to user bookings
      if (!isAdmin) {
        navigate('/bookings');
      }
    }

    // If notification has a booking_id and court_id, could also navigate to specific booking calendar
    // For now, keeping it simple with the bookings list pages
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-600 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[600px] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllAsReadMutation.mutate()}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                      !notification.is_read ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      {/* Icon */}
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <p className="text-sm font-semibold text-gray-900">
                            {notification.title}
                          </p>
                          {!notification.is_read && (
                            <div className="w-2 h-2 bg-blue-600 rounded-full ml-2 mt-1"></div>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {notification.message}
                        </p>
                        {notification.court_name && (
                          <p className="text-xs text-gray-500 mt-1">
                            Court: {notification.court_name}
                          </p>
                        )}
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-xs text-gray-400">
                            {formatDistanceToNow(new Date(notification.created_at), {
                              addSuffix: true,
                            })}
                          </p>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!deleteNotificationMutation.isLoading) {
                                deleteNotificationMutation.mutate(notification.id);
                              }
                            }}
                            disabled={deleteNotificationMutation.isLoading}
                            className="text-xs text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
                            title="Delete notification"
                          >
                            <Trash2 className="w-3 h-3" />
                            <span>Delete</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;

